'use client';

import { MasterQuoteForm } from '@/components/master-quote-form';
import { RotatingHeadline } from '@/components/RotatingHeadline';
import styles from '@/app/(marketing)/chattel-mortgage/page.module.css';

// Static lender logos data
const lenderLogos = [
  'angle finance_1757571445308.png',
  'bank of queensland_1757571445309.png',
  'branded financial services_1757571445309.png',
  'flexi commercial_1757571445303.png',
  'metro finance_1757571445304.png',
  'moneyme autopay_1757571445305.png',
  'pepper money_1757571445305.png',
  'plenti finance commercial_1757571445306.png',
  'resimac_1757571445306.png',
  'shift asset finance_1757571445307.png',
  'veya money allied credit_1757571445307.png',
  'westpac chattel mortgage_1757571445308.png',
];

const reviews = [
  {
    name: 'Nicole Dewberry',
    company: 'The Tax Tailor',
    text: 'I couldn\'t be more pleased with the service I received from Francis and Alex at Valco Group Financial Services. For the first time, my asset finance experience was truly seamless. From the initial application to the receipt of funds, I was treated with exceptional care and professionalism. Their communication and follow-up were outstanding, making me feel like a truly valued client. I will definitely be using their services again and highly recommend them to anyone seeking asset finance.',
    initial: 'N'
  },
  {
    name: 'Cody Davies',
    company: 'Gold Coast Building Maintenance',
    text: 'Had Alex handle the finance for our new business vehicle and he did a fantastic job. Communication was very good and he really made the whole process easy to deal with. He bounced back and forward to get the best deal he could and can tell he cares about what he does. Can\'t thank him enough, 100% will be using again for any further things of this nature.',
    initial: 'C'
  },
  {
    name: 'Brooke Murnane',
    company: 'LKO Electrical',
    text: 'So glad we met Francis! He has helped us find 3 vehicles in the last 12 months the newest addition for my business arriving today. Professional, and down to earth, and simply just makes the whole process easy! We will be using Francis for all our vehicle and finance advice and needs. A+++++',
    initial: 'B'
  }
];

const benefits = [
  { icon: '💰', text: 'Claim GST on purchase price upfront - instant tax savings' },
  { icon: '📊', text: 'Claim depreciation & interest as tax deductions yearly' },
  { icon: '🏆', text: 'Own the vehicle from day one - build business equity' },
  { icon: '🔒', text: 'Fixed repayments - budget with confidence' },
  { icon: '⚡', text: 'Approval in 24 hours - get on the road fast' },
  { icon: '🎯', text: 'Tailored for ABN holders & business owners' }
];

const faqs = [
  {
    question: "Do I need a deposit for vehicle finance?",
    answer: "In most cases, no deposit is required. Many vehicle finance applications are approved with zero deposit, depending on the vehicle, structure, and overall application."
  },
  {
    question: "Can I finance a used vehicle?",
    answer: "Yes. New and used vehicles of any age can be financed, subject to the vehicle and lender being matched correctly. Dealer purchases, private sales, and auction purchases are all supported."
  },
  {
    question: "Can I get vehicle finance without providing tax returns?",
    answer: "Yes. Low-doc vehicle finance is available with no financial documents required for loans up to $250,000, depending on the lender and structure."
  },
  {
    question: "What types of vehicles can I finance?",
    answer: "We arrange finance for most vehicles used by Australian businesses. This includes cars, SUVs, utes, vans, and vehicles with business fit-outs. Dealer, private sale, and auction purchases are all supported."
  },
  {
    question: "Can a new business or new ABN get vehicle finance?",
    answer: "Yes. We can arrange vehicle finance for businesses that have been operating for as little as one day. Approvals are based on experience, structure, and the overall deal rather than time in business alone."
  },
  {
    question: "How long does vehicle finance approval take?",
    answer: "Most vehicle finance applications are approved within the same business day once the required details are provided."
  },
  {
    question: "What interest rates can I expect on vehicle finance?",
    answer: "Interest rates vary based on the vehicle, age, business profile, and credit position. We compare multiple lenders to find the most suitable option rather than offering a single rate."
  },
  {
    question: "What is the difference between a Chattel Mortgage and a Finance Lease?",
    answer: "With a Chattel Mortgage, the business owns the vehicle from day one and GST is usually claimed upfront. With a Finance Lease, the lender owns the vehicle during the term and the business makes fixed lease repayments, with options available at the end of the lease."
  },
  {
    question: "Can I get vehicle finance if I have bad credit or ATO debt?",
    answer: "Yes, in many cases. There are lenders outside of the major banks that specialise in vehicle finance for businesses with credit issues or ATO debt, and many deals are still achievable."
  }
];

export default function ChattelMortgageContent() {
  const stats = [
    { value: '$10K – $2M+', label: 'Finance Available' },
    { value: '1–7 Years', label: 'Flexible Terms' },
    { value: '24 Hours', label: 'Typical Approval' },
    { value: '50+', label: 'Lender Panel' }
  ];

  return (
    <div className={styles.vgfsChattelMortgage}>
      {/* 1) Quote Form Section */}
      <section id="quote" className={styles.vgfsQuoteSection}>
        <MasterQuoteForm page="chattel-mortgage" />
      </section>

      {/* 2) Metrics Banner - Matches Truck Page */}
      <section className="py-12" style={{ backgroundColor: '#0F2A44' }}>
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {stats.map((stat) => (
              <div key={stat.label}>
                <div className="text-2xl md:text-3xl font-bold text-orange-500" style={{ fontFamily: 'var(--font-poppins)', fontWeight: 700 }}>
                  {stat.value}
                </div>
                <div className="text-slate-200 mt-1 text-sm md:text-base">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3) 3 Simple Steps Section - Car Version */}
      <section className={styles.vgfsProcess}>
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-slate-900" style={{ fontFamily: 'var(--font-poppins)', fontWeight: 800 }}>
            Get Your Chattel Mortgage Approved in <span className="text-orange-500">3 Simple Steps</span>
          </h2>
          <div className={styles.vgfsProcessGrid}>
            <div className={styles.vgfsProcessCard} data-testid="process-step-1">
              <div className={styles.vgfsProcessNumber}>1</div>
              <h3 className="text-lg font-bold text-white mb-2">Get Your Quote</h3>
              <p className="text-slate-300">Complete our 60-second form. No upfront credit checks. Only basic details required.</p>
            </div>
            <div className={styles.vgfsProcessCard} data-testid="process-step-2">
              <div className={styles.vgfsProcessNumber}>2</div>
              <h3 className="text-lg font-bold text-white mb-2">Compare Options</h3>
              <p className="text-slate-300">Your expert broker compares 50+ lenders to find the best deal for your situation.</p>
            </div>
            <div className={styles.vgfsProcessCard} data-testid="process-step-3">
              <div className={styles.vgfsProcessNumber}>3</div>
              <h3 className="text-lg font-bold text-white mb-2">Get Approved & Settle</h3>
              <p className="text-slate-300">Same-day pre-approvals. Settlement typically within 24 to 48 hours so you can get back on the road.</p>
            </div>
          </div>
        </div>
      </section>

      {/* 4) Lender Carousel - Positioned exactly below 3 steps */}
      <section className={styles.vgfsLenders}>
        <h2>50+ TRUSTED LENDERS COMPETING FOR YOUR BUSINESS</h2>
        <div className={styles.vgfsLendersCarousel}>
          {/* Duplicate for seamless loop */}
          {[...lenderLogos, ...lenderLogos].map((logo, index) => (
            <div key={index} className={styles.vgfsLenderLogo}>
              <img 
                src={`/lender-logos/${logo}`} 
                alt={logo.replace(/_\d+\.png$/, '').replace(/_/g, ' ')}
                loading={index < 6 ? "eager" : "lazy"}
                width="180"
                height="60"
                style={{ width: 'auto', height: 'auto', maxWidth: '180px', maxHeight: '60px' }}
              />
            </div>
          ))}
        </div>
      </section>

      {/* 5) Why Businesses Choose VALCO GROUP - Matches Truck Page EXACTLY */}
      <section className={`${styles.vgfsBenefits} py-20 md:py-24`}>
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-white" style={{ fontFamily: 'var(--font-poppins)', fontWeight: 800 }}>
            Why Businesses Choose <span className="text-orange-500">VALCO GROUP</span>
          </h2>
          <div className={styles.vgfsBenefitsGrid}>
            <div className={`${styles.vgfsBenefitCard} !text-slate-900`} data-testid="benefit-card-0">
              <div className={styles.vgfsBenefitIcon}>💰</div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Competitive Rates</h3>
              <p className="text-slate-900 font-medium leading-relaxed">From 5.49% p.a. for qualifying borrowers. We compare 50+ lenders to find you a suitable rate for your situation.</p>
            </div>
            <div className={`${styles.vgfsBenefitCard} !text-slate-900`} data-testid="benefit-card-1">
              <div className={styles.vgfsBenefitIcon}>📋</div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Low-Doc Friendly</h3>
              <p className="text-slate-900 font-medium leading-relaxed">Finance up to $250k with no financial documents required. No tax returns. No bank statements.</p>
            </div>
            <div className={`${styles.vgfsBenefitCard} !text-slate-900`} data-testid="benefit-card-2">
              <div className={styles.vgfsBenefitIcon}>⚡</div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Same-Day Approvals</h3>
              <p className="text-slate-900 font-medium leading-relaxed">Pre-approval within hours, not days. Settlement typically within 24 to 48 hours.</p>
            </div>
            <div className={`${styles.vgfsBenefitCard} !text-slate-900`} data-testid="benefit-card-3">
              <div className={styles.vgfsBenefitIcon}>🛡️</div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Expert Brokers</h3>
              <p className="text-slate-900 font-medium leading-relaxed">Your deal is handled by senior brokers only. Each broker has 10+ years' experience. No juniors. No call centres.</p>
            </div>
          </div>
        </div>
      </section>

      {/* 6) Finance for Every Vehicle Section */}
      <section className="py-20 md:py-24 bg-white border-b border-slate-100">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-8 text-slate-900" style={{ fontFamily: 'var(--font-poppins)', fontWeight: 800 }}>
            Finance Any Vehicle <span className="text-orange-500">Your Business</span> Needs
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {/* TILE 1 — Car & SUV */}
            <div className="bg-slate-50 rounded-xl p-6 border border-slate-200 hover:border-orange-300 hover:shadow-md transition-all flex flex-col h-full">
              <div className="h-16 w-16 flex items-center justify-center mb-4 mx-auto text-[48px] leading-none" role="img" aria-label="Car icon">
                🚗
              </div>
              <h3 className="text-2xl font-bold mb-3 text-slate-900 text-center" style={{ fontFamily: 'var(--font-poppins)' }}>
                Car & SUV
              </h3>
              <ul className="space-y-2 mt-auto">
                <li className="flex items-center gap-2">
                  <span className="text-orange-500 font-bold">✓</span>
                  <span className="text-slate-900 font-medium leading-relaxed">New & used vehicles</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-orange-500 font-bold">✓</span>
                  <span className="text-slate-900 font-medium leading-relaxed">Business & personal use accepted</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-orange-500 font-bold">✓</span>
                  <span className="text-slate-900 font-medium leading-relaxed">Electric & hybrid vehicles included</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-orange-500 font-bold">✓</span>
                  <span className="text-orange-500 font-medium leading-relaxed">No age limits on vehicles</span>
                </li>
              </ul>
            </div>

            {/* TILE 2 — Utes */}
            <div className="bg-slate-50 rounded-xl p-6 border border-slate-200 hover:border-orange-300 hover:shadow-md transition-all flex flex-col h-full">
              <div className="h-16 w-16 flex items-center justify-center mb-4 mx-auto text-[48px] leading-none" role="img" aria-label="Ute icon">
                🛻
              </div>
              <h3 className="text-2xl font-bold mb-3 text-slate-900 text-center" style={{ fontFamily: 'var(--font-poppins)' }}>
                Utes
              </h3>
              <ul className="space-y-2 mt-auto">
                <li className="flex items-center gap-2">
                  <span className="text-orange-500 font-bold">✓</span>
                  <span className="text-slate-900 font-medium leading-relaxed">Single cab, dual cab & crew cab</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-orange-500 font-bold">✓</span>
                  <span className="text-slate-900 font-medium leading-relaxed">Trades, contractors & fleet vehicles</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-orange-500 font-bold">✓</span>
                  <span className="text-slate-900 font-medium leading-relaxed">New or used purchases</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-orange-500 font-bold">✓</span>
                  <span className="text-orange-500 font-medium leading-relaxed">No age limits on vehicles</span>
                </li>
              </ul>
            </div>

            {/* TILE 3 — Vans */}
            <div className="bg-slate-50 rounded-xl p-6 border border-slate-200 hover:border-orange-300 hover:shadow-md transition-all flex flex-col h-full">
              <div className="h-16 w-16 flex items-center justify-center mb-4 mx-auto text-[48px] leading-none" role="img" aria-label="Van icon">
                🚐
              </div>
              <h3 className="text-2xl font-bold mb-3 text-slate-900 text-center" style={{ fontFamily: 'var(--font-poppins)' }}>
                Vans
              </h3>
              <ul className="space-y-2 mt-auto">
                <li className="flex items-center gap-2">
                  <span className="text-orange-500 font-bold">✓</span>
                  <span className="text-slate-900 font-medium leading-relaxed">Delivery, trade & service vans</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-orange-500 font-bold">✓</span>
                  <span className="text-slate-900 font-medium leading-relaxed">Petrol, diesel & electric options</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-orange-500 font-bold">✓</span>
                  <span className="text-slate-900 font-medium leading-relaxed">Fit-outs can be included in finance</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-orange-500 font-bold">✓</span>
                  <span className="text-orange-500 font-medium leading-relaxed">No age limits on vehicles</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 7) Recent Vehicle Finance Approvals Section */}
      <section className={`${styles.vgfsBenefits} py-20 md:py-24`} style={{ backgroundColor: '#0F2A44' }}>
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-6 text-white" style={{ fontFamily: 'var(--font-poppins)', fontWeight: 800 }}>
            Recent <span className="text-orange-500">Chattel Mortgage</span> Approvals
          </h2>
          <p className="text-center text-slate-300 mb-16 max-w-2xl mx-auto">
            Real examples of vehicle finance we&apos;ve arranged for Australian businesses in recent months.
          </p>
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {/* CARD 1 */}
            <div className="bg-white/10 rounded-xl p-6 border border-white/20 shadow-lg">
              <div className="text-3xl font-bold text-orange-500 mb-2">$150,000</div>
              <div className="font-semibold text-white mb-1">Toyota LandCruiser 2025</div>
              <div className="text-slate-300 text-sm mb-1">Agriculture Business</div>
              <div className="text-slate-400 text-sm mb-3">Regional VIC</div>
              <div className="flex items-center justify-between pt-3 border-t border-white/20">
                <span className="text-green-400 font-semibold text-sm flex items-center gap-1">
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Approved in 24 hours
                </span>
                <span className="text-xs bg-white/20 text-white px-2 py-1 rounded">Chattel Mortgage</span>
              </div>
            </div>

            {/* CARD 2 */}
            <div className="bg-white/10 rounded-xl p-6 border border-white/20 shadow-lg">
              <div className="text-3xl font-bold text-orange-500 mb-2">$320,000</div>
              <div className="font-semibold text-white mb-1">Mercedes G-Wagon 2024</div>
              <div className="text-slate-300 text-sm mb-1">Ecommerce Business</div>
              <div className="text-slate-400 text-sm mb-3">Gold Coast, QLD</div>
              <div className="flex items-center justify-between pt-3 border-t border-white/20">
                <span className="text-green-400 font-semibold text-sm flex items-center gap-1">
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Same-day approval
                </span>
                <span className="text-xs bg-white/20 text-white px-2 py-1 rounded">Chattel Mortgage</span>
              </div>
            </div>

            {/* CARD 3 */}
            <div className="bg-white/10 rounded-xl p-6 border border-white/20 shadow-lg">
              <div className="text-3xl font-bold text-orange-500 mb-2">$35,000</div>
              <div className="font-semibold text-white mb-1">Suzuki Jimny 2023</div>
              <div className="text-slate-300 text-sm mb-1">Photographer</div>
              <div className="text-slate-400 text-sm mb-3">Perth, WA</div>
              <div className="flex items-center justify-between pt-3 border-t border-white/20">
                <span className="text-green-400 font-semibold text-sm flex items-center gap-1">
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Approved in 24 hours
                </span>
                <span className="text-xs bg-white/20 text-white px-2 py-1 rounded">Low-Doc Finance</span>
              </div>
            </div>
          </div>

          <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-6 relative z-10">
            <a
              href="#quote"
              className="w-full md:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-orange-500 px-8 py-4 text-white text-lg font-semibold hover:bg-orange-600 transition-all shadow-lg hover:shadow-orange-500/20"
            >
              Get Your Free Quote
              <span className="text-xl">→</span>
            </a>
            <a
              href="tel:+61384569966"
              className="w-full md:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-transparent border-2 border-white px-8 py-4 text-white text-lg font-semibold hover:bg-white/10 transition-all"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              Call 03 8456 9966
            </a>
          </div>
        </div>
      </section>

      {/* 8) Reviews Section - Positioned above FAQ */}
      <section className={`${styles.vgfsReviews} ${styles.vgfsBenefits} pt-14 md:pt-16 pb-20 md:pb-24`} style={{ backgroundColor: '#0F2A44' }}>
        <div className="container mx-auto px-4 relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold text-center mt-0 mb-6 text-white" style={{ fontFamily: 'var(--font-poppins)', fontWeight: 800 }}>
            Trusted by <span className="text-orange-500">2,000+</span> Business Owners
          </h2>
          <div className={styles.vgfsGoogleReviewsLogo}>
            <svg width="100%" height="50" viewBox="0 0 420 50" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid meet" style={{ maxWidth: '420px' }}>
              {/* Google "G" Icon */}
              <path d="M27.5 19.5C27.5 18.4 27.4 17.4 27.2 16.4H15V22.2H22C21.7 23.9 20.7 25.4 19.2 26.4V30H23.5C25.9 27.8 27.5 24.3 27.5 19.5Z" fill="#4285F4"/>
              <path d="M15 34C18.8 34 22 32.7 23.5 30L19.2 26.4C18.1 27.2 16.7 27.7 15 27.7C11.3 27.7 8.2 25.5 7.2 22.3H2.8V26C4.7 29.7 9.5 34 15 34Z" fill="#34A853"/>
              <path d="M7.2 22.3C7 21.5 6.9 20.8 6.9 20C6.9 19.2 7 18.5 7.2 17.7V14H2.8C2 15.7 1.5 17.8 1.5 20C1.5 22.2 2 24.3 2.8 26L7.2 22.3Z" fill="#FBBC04"/>
              <path d="M15 12.3C16.9 12.3 18.6 13 20 14.3L23.8 10.5C22 8.8 18.8 6 15 6C9.5 6 4.7 10.3 2.8 14L7.2 17.7C8.2 14.5 11.3 12.3 15 12.3Z" fill="#EA4335"/>
              {/* "Google Reviews" Text */}
              <text x="38" y="24" fontFamily="Arial, Helvetica, sans-serif" fontSize="19" fontWeight="700" fill="#ffffff">Google</text>
              <text x="38" y="36" fontFamily="Arial, Helvetica, sans-serif" fontSize="11" fontWeight="400" fill="#cbd5e1">Reviews</text>
              {/* Star Rating */}
              <text x="120" y="28" fontFamily="Arial, sans-serif" fontSize="22" fontWeight="600" fill="#fbbc04">★★★★★</text>
              {/* Rating Text */}
              <text x="235" y="28" fontFamily="Arial, sans-serif" fontSize="16" fontWeight="500" fill="#ffffff">5.0 from 180+ Reviews</text>
            </svg>
          </div>
          
          <div className={styles.vgfsReviewsGrid}>
            {reviews.map((review, idx) => (
              <div key={idx} className={styles.vgfsReviewCard} data-testid={`review-card-${idx}`}>
                <div className={styles.vgfsReviewHeader}>
                  <div className={styles.vgfsReviewAvatar}>{review.initial}</div>
                  <div>
                    <div className={styles.vgfsReviewName}>{review.name}</div>
                    <div className={styles.vgfsReviewCompany}>{review.company}</div>
                    <div className={styles.vgfsReviewStars}>★★★★★</div>
                  </div>
                </div>
                <p className={styles.vgfsReviewText}>&quot;{review.text}&quot;</p>
              </div>
            ))}
          </div>
          
          <div className="flex justify-center mt-12 mb-12">
            <a href="https://share.google/T3kV2urBWYqxbRvlR" target="_blank" rel="noopener" className="text-white font-bold text-xl hover:text-orange-400 transition-colors underline underline-offset-8 decoration-2 decoration-orange-500/30 hover:decoration-orange-500" data-testid="link-write-review">
              Read All Reviews on Google
            </a>
          </div>
        </div>
      </section>

      {/* 9) FAQ Section - Cloned from Truck Page */}
      <section className="py-20 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-slate-900" style={{ fontFamily: 'var(--font-poppins)', fontWeight: 800 }}>
            Frequently Asked <span className="text-orange-500">Questions</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-6 max-w-7xl mx-auto">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-xl p-6 border border-slate-200 hover:shadow-md transition-shadow">
                <h3 className="text-lg font-bold text-slate-900 mb-3 leading-tight" style={{ fontFamily: 'var(--font-poppins)' }}>
                  {faq.question}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>

          <div className="flex flex-col items-center mt-12">
            <a
              href="#quote"
              className="w-full md:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-orange-500 px-10 py-4 text-white text-lg font-semibold hover:bg-orange-600 transition-all shadow-lg hover:shadow-orange-500/20"
            >
              Get Your Free Quote
              <span className="text-xl">→</span>
            </a>
            <p className="mt-4 text-slate-500 text-sm font-medium">
              Takes 60 seconds. No upfront credit checks.
            </p>
            <p className="mt-6 text-slate-600 text-sm">
              Based in Victoria? Read our guide to{' '}
              <a href="/chattel-mortgage-melbourne" className="text-orange-600 font-semibold underline underline-offset-4 hover:text-orange-700">chattel mortgage finance in Melbourne</a>.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}